import React, { MouseEvent, useState, JSX } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import useTranslation from '../../Hooks/useTranslation';
import fallback from '../../../_Translations/generated/flight-segment-details-popup.en.t.json';
import Keys from '../../../_Translations/generated/flight-segment-details-popup.en.t.json.keys';
import { ApiCabinClass, ApiFlightItem, ApiFlightLeg, ApiLocation } from '@ibe/api';
import DateDisplay from '../../Components/UI/DateDisplay/DateDisplay';
import useMediaQuery, { MEDIAQUERY_DEFAULTS } from '../../Hooks/useMediaQuery';
import { useTheme } from '@emotion/react';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import useConfig from '../../Hooks/useConfig';

interface Props {
  flight: ApiFlightItem;
  iconButton?: boolean;
}

const FlightSegmentDetailsPopup = (props: Props) => {
  const { t } = useTranslation('flight-segment-details-popup', fallback, Keys);
  const isMobile = useMediaQuery({ type: 'max', query: MEDIAQUERY_DEFAULTS.md });
  const { flight, iconButton } = props;

  const theme = useTheme();
  const config = useConfig();
  const { airlineLogoPlaceholder } = theme;

  const getFlightClass = (flightItem: ApiFlightItem): ApiCabinClass | undefined => {
    if (flightItem?.fare && flightItem?.fare?.length > 0) {
      return flightItem.fare[0].compartment;
    }
    return undefined;
  };

  const flightClass = getFlightClass(flight);
  const segment = flight?.segment.at(0) || undefined;

  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const toggleDetailsPopup = () => setShowDetailsPopup(prevState => !prevState);

  const AirlineLogo = ({ flightLeg }: { flightLeg: ApiFlightLeg }): JSX.Element => {
    return (
      <div className={'flight-segment-details__modal__logo mr-3'}>
        {flightLeg.marketingCarrier?.logoUrl && (
          <img src={flightLeg.marketingCarrier?.logoUrl} alt="Airline logo" />
        )}
        {!flightLeg.marketingCarrier?.logoUrl && airlineLogoPlaceholder && (
          <img src={airlineLogoPlaceholder} alt="Airline logo" />
        )}
      </div>
    );
  };

  const FlightDate = ({ flightLeg }: { flightLeg: ApiFlightLeg }): JSX.Element => {
    return (
      <Col>
        <DateDisplay utc format={'dddd D MMMM YYYY'} date={flightLeg.departure} />
      </Col>
    );
  };

  const AirportInfo = ({
    title,
    airport,
    date,
    dateDeviation = 0
  }: {
    title: string;
    date: string;
    airport: ApiLocation;
    dateDeviation?: number;
  }): JSX.Element => {
    return isMobile ? (
      <p>
        <span>{title}</span>
        <span className="time">
          <DateDisplay utc format={'HH:mm'} date={date} />
          {dateDeviation > 0 && <sup>+{dateDeviation}</sup>}
        </span>
        <span className="code">{airport.code}</span>
        <span>{airport.description}</span>
      </p>
    ) : (
      <Row className="mb-2">
        <Col xs={2}>{title}</Col>
        <Col xs={2} className="time">
          <DateDisplay utc format={'HH:mm'} date={date} />
          {dateDeviation > 0 && <sup>+{dateDeviation}</sup>}
        </Col>
        <Col xs={1} className="code">
          {airport.code}
        </Col>
        <Col xs={7}>{airport.description}</Col>
      </Row>
    );
  };

  const AirportInfos = ({ flightLeg }: { flightLeg: ApiFlightLeg }) => {
    return (
      <Col>
        <AirportInfo
          title={t(Keys.departure)}
          airport={flightLeg.origin}
          date={flightLeg.departure}
        />
        <AirportInfo
          title={t(Keys.arrival)}
          airport={flightLeg.destination}
          date={flightLeg.arrival}
          dateDeviation={flightLeg.dateDeviation}
        />
        {flightClass && (
          <Row>
            <Col>{getFlightClassDescription(flightClass)}</Col>
          </Row>
        )}
      </Col>
    );
  };

  const getFlightClassDescription = (cabinClass: ApiCabinClass): string => {
    switch (cabinClass) {
      case ApiCabinClass.ECONOMY:
        return t(Keys.cabinClass.economy);
        break;
      case ApiCabinClass.PREMIUMECONOMY:
        return t(Keys.cabinClass.premiumEconomy);
        break;
      case ApiCabinClass.BUSINESS:
        return t(Keys.cabinClass.business);
        break;
      case ApiCabinClass.FIRST:
        return t(Keys.cabinClass.first);
        break;
      default:
        return '';
        break;
    }
  };

  const AirlineInfo = ({ flightLeg }: { flightLeg: ApiFlightLeg }): JSX.Element => {
    return (
      <Col>
        {(flightLeg.marketingCarrier?.name || flightLeg.marketingCarrier?.code) +
          ' - ' +
          flightLeg.flightCode +
          flightLeg.flightNumber}
      </Col>
    );
  };

  return (
    <div
      className="flight-segment-details"
      onClick={(e: MouseEvent<HTMLDivElement>): void => {
        e.stopPropagation();
      }}
    >
      {segment && (
        <span
          onClick={toggleDetailsPopup}
          className={`${iconButton ? 'flight-segment-details__icon' : ''}`}
        >
          {iconButton ? <Icon icon={faInfoCircle} /> : t(Keys.details)}
        </span>
      )}
      <Modal
        className="flight-segment-details__modal"
        isOpen={showDetailsPopup}
        toggle={toggleDetailsPopup}
        container={
          config.outsideElementsContainerId
            ? window?.document?.getElementById(config.outsideElementsContainerId) || undefined
            : undefined
        }
      >
        <ModalHeader>{t(Keys.title)}</ModalHeader>
        <ModalBody>
          {segment &&
            segment.legs.map((flightLeg: ApiFlightLeg, idx: number) => {
              return (
                <div className="mb-3" key={`flightLeg-${idx}`}>
                  <div className="d-flex mb-3">
                    {<AirlineLogo flightLeg={flightLeg} />}
                    <div>
                      <Row>{<FlightDate flightLeg={flightLeg} />}</Row>
                      <Row>
                        <AirlineInfo flightLeg={flightLeg} />
                      </Row>
                    </div>
                  </div>
                  <Row className="ml-0 ml-md-3 pl-3 flight-segment-details__modal__flight-infos">
                    <AirportInfos flightLeg={flightLeg} />
                  </Row>
                </div>
              );
            })}
        </ModalBody>
        <ModalFooter className="flight-segment-details__modal__footer">
          <Button onClick={toggleDetailsPopup}>{t(Keys.close)}</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default FlightSegmentDetailsPopup;
